import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem } from "namirasoft-core";
import { FuelRow, NamirasoftERPMetaDatabase, VehicleInputRow, VehicleOdometerUnit, VehicleRow, VehicleTankCapacityUnit } from "namirasoft-erp";
import { NSBox, NSBoxEntity, NSBoxEnum, NSBoxInteger, NSBoxString, NSBoxTextArea, NSColumn, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpEntities } from "../ErpEntities";
import { ErpLayout } from '../ErpLayout';
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpScope, ErpScopeField, ErpScopeMode } from "../ErpScope";

interface Props extends NSARouterMakerResult, ErpRouterProps { isEdit: boolean; }

export default class VehicleEditPage extends Component<Props>
{
    Fuel = createRef<NSBoxEntity<FuelRow>>();
    Name = createRef<NSBoxString>();
    LicensePlate = createRef<NSBoxString>();
    VIN = createRef<NSBoxString>();
    ModelYear = createRef<NSBoxInteger>();
    Color = createRef<NSBoxString>();
    OdometerUnit = createRef<NSBoxEnum<VehicleOdometerUnit>>();
    TankCapacityValue = createRef<NSBoxInteger>();
    TankCapacityUnit = createRef<NSBoxEnum<VehicleTankCapacityUnit>>();
    Description = createRef<NSBoxTextArea>();

    override render()
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);

        return (
            <ErpLayout<VehicleRow, VehicleInputRow>
                {...this.props}
                mode={ErpScopeMode.Select}
                fields={[ErpScopeField.Company, ErpScopeField.Branch]}
                pages={{
                    entity: ErpEntities.vehicle(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): VehicleInputRow => ({
                                        company_id: ErpScope.getCompanyId() ?? "",
                                        branch_id: ErpScope.getBranchId() ?? "",
                                        fuel_id: this.Fuel.current?.getValueOne() ?? "",
                                        name: this.Name.current?.getValue() ?? "",
                                        license_plate: this.LicensePlate.current?.getValue() ?? "",
                                        vin: this.VIN.current?.getValue() ?? "",
                                        model_year: this.ModelYear.current?.getValue() ?? 0,
                                        color: this.Color.current?.getValue() ?? "",
                                        odometer_unit: this.OdometerUnit.current?.getValueEnumOne(VehicleOdometerUnit, VehicleOdometerUnit.KM) ?? VehicleOdometerUnit.KM,
                                        tank_capacity_value: this.TankCapacityValue.current?.getValue() ?? 0,
                                        tank_capacity_unit: this.TankCapacityUnit.current?.getValueEnumOne(VehicleTankCapacityUnit, VehicleTankCapacityUnit.Liter) ?? VehicleTankCapacityUnit.Liter,
                                        description: this.Description.current?.getValue() ?? null,
                                        vehicle_category: [],
                                        vehicle_tag: [],
                                        vehicle_field: [],
                                    }),
                                    setRow: (row: VehicleRow) =>
                                    {
                                        ErpScope.set({ company_id: row.company_id, branch_id: row.branch_id });
                                        this.Fuel.current?.setValueOne(row.fuel_id);
                                        this.Name.current?.setValue(row.name);
                                        this.LicensePlate.current?.setValue(row.license_plate);
                                        this.VIN.current?.setValue(row.vin);
                                        this.ModelYear.current?.setValue(row.model_year);
                                        this.Color.current?.setValue(row.color);
                                        this.OdometerUnit.current?.setValueOne(row.odometer_unit);
                                        this.TankCapacityValue.current?.setValue(row.tank_capacity_value);
                                        this.TankCapacityUnit.current?.setValueOne(row.tank_capacity_unit);
                                        this.Description.current?.setValue(row.description);
                                    }
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxEntity<FuelRow>
                        ref={this.Fuel} title="Fuel" info={{ link: "https://namirasoft.com/erp/console-guide/vehicle/#field-fuel-id" }} required multiple={false}
                        table_name="fuel" table={db.tables.fuel}
                        list={(filters, page, size, sorts) =>
                        {
                            let f: FilterItem[] = filters ? [...filters] : [];
                            f = ErpScope.pushFilter(f, db.tables.fuel, "company_id", ErpScope.getCompanyId());
                            f = ErpScope.pushFilter(f, db.tables.fuel, "branch_id", ErpScope.getBranchId());
                            return this.props.servers.erp.fuel.List(f, page, size, sorts, null);
                        }}
                        getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxString ref={this.Name} required title="Name" info={{ link: "https://namirasoft.com/erp/console-guide/vehicle/#field-name" }} style={{ width: NSBox.width.double }} />
                    <NSRow style={{ gap: 8 }}>
                        <NSBoxString ref={this.LicensePlate} required title="License Plate" info={{ link: "https://namirasoft.com/erp/console-guide/vehicle/#field-license-plate" }} style={{ width: NSBox.width.one }} />
                        <NSBoxString ref={this.VIN} required title="VIN" info={{ link: "https://namirasoft.com/erp/console-guide/vehicle/#field-vin" }} style={{ width: NSBox.width.one }} />
                    </NSRow>
                    <NSRow style={{ gap: 8 }}>
                        <NSBoxInteger ref={this.ModelYear} required title="Model Year" info={{ link: "https://namirasoft.com/erp/console-guide/vehicle/#field-model-year" }} style={{ width: NSBox.width.one }} />
                        <NSBoxString ref={this.Color} required title="Color" info={{ link: "https://namirasoft.com/erp/console-guide/vehicle/#field-color" }} style={{ width: NSBox.width.one }} />
                    </NSRow>
                    <NSBoxEnum<VehicleOdometerUnit>
                        ref={this.OdometerUnit} title="Odometer Unit" info={{ link: "https://namirasoft.com/erp/console-guide/vehicle/#field-odometer-unit" }} required multiple={false}
                        getEnumObject={() => VehicleOdometerUnit}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSRow style={{ gap: 8 }}>
                        <NSBoxInteger ref={this.TankCapacityValue} required title="Tank Capacity Value" info={{ link: "https://namirasoft.com/erp/console-guide/vehicle/#field-tank-capacity-value" }} style={{ width: NSBox.width.one }} />
                        <NSBoxEnum<VehicleTankCapacityUnit>
                            ref={this.TankCapacityUnit} title="Tank Capacity Unit" info={{ link: "https://namirasoft.com/erp/console-guide/vehicle/#field-tank-capacity-unit" }} required multiple={false}
                            getEnumObject={() => VehicleTankCapacityUnit}
                            style={{ width: NSBox.width.one }}
                        />
                    </NSRow>
                    <NSBoxTextArea ref={this.Description} required={false} title="Description" info={{ link: "https://namirasoft.com/erp/console-guide/vehicle/#field-description" }} style={{ width: NSBox.width.double }} />
                </NSColumn>
            </ErpLayout>
        );
    }
}